import React, { useState, useEffect } from "react";
import { Row, Col, Alert, Spinner } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import { getCDSettlementDetails, completeSettlementBatch } from "../../../../services/adminApi";

const SettlementDetailsView = ({ settlementId, onBack }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [utrNumber, setUtrNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("NEFT");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDetails();
  }, [settlementId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await getCDSettlementDetails(settlementId);
      if (res.data?.success && res.data?.data) {
        setDetails(res.data.data);
        if (res.data.data.settlement?.utr_number) {
          setUtrNumber(res.data.data.settlement.utr_number);
        }
        if (res.data.data.settlement?.payment_date) {
          setPaymentDate(res.data.data.settlement.payment_date);
        }
        if (res.data.data.settlement?.remarks) {
          setRemarks(res.data.data.settlement.remarks);
        }
      } else {
        setErrorMsg("Settlement details record not found.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.message || "Failed to load settlement details.");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveComplete = async () => {
    if (!utrNumber.trim()) {
      alert("Please enter Bank UTR / Transaction Reference Number.");
      return;
    }

    try {
      setSubmitting(true);
      setErrorMsg("");
      const res = await completeSettlementBatch({
        batchId: settlementId,
        utrNumber,
        paymentDate,
        paymentMode,
        remarks,
        paymentStatus: "Mark as Paid / Complete",
      });

      if (res.data?.success) {
        setSuccessMsg(res.data.message || "Payment approved and settlement marked as completed!");
        fetchDetails();
      } else {
        setErrorMsg(res.data?.message || "Failed to update settlement.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.message || "Failed to complete settlement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" variant="danger" /></div>;
  }

  const settlement = details?.settlement || {};
  const bankDetails = details?.bankDetails || {};
  const txList = details?.transactions || [];

  const initiatedAmount = Number(
    settlement.net_payable_amount || 
    settlement.paid_amount || 
    settlement.commission_amount || 
    0
  );

  return (
    <div className="bma-settlement-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="bma-breadcrumb">
            Home &nbsp;&gt;&nbsp; Payment Settlement &nbsp;&gt;&nbsp; <strong>Settlement Details</strong>
          </div>
          <div className="d-flex align-items-center gap-3 mt-1">
            <h1 className="bma-page-title mb-0">Settlement Details</h1>
            <span className="bma-badge badge-warning">{settlement.status || "Initiated"}</span>
          </div>
        </div>
        <button type="button" className="bma-btn-outline" onClick={onBack}>
          <FiArrowLeft /> Back to List
        </button>
      </div>

      {successMsg && <Alert variant="success">{successMsg}</Alert>}
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      {/* BANNER CARD */}
      <div className="bma-card mb-4">
        <div className="bma-card-body">
          <Row className="align-items-center g-3">
            <Col md={3}>
              <h2 className="bma-card-title mb-0">{bankDetails.account_holder_name || `Casting Director #${settlement.casting_director_id}`}</h2>
              <div style={{ fontFamily: "monospace", fontWeight: "700", color: "#60a5fa" }}>{settlement.batch_code || "—"}</div>
              <small style={{ color: "#94a3b8" }}>Period: {settlement.settlement_period || "—"}</small>
            </Col>

            <Col md={3}>
              <div className="bma-metric-lbl">Total Transactions</div>
              <div className="bma-metric-val">{settlement.total_transactions_count || txList.length}</div>
            </Col>

            <Col md={3}>
              <div className="bma-metric-lbl">Initiated Settlement Amount</div>
              <div className="bma-metric-val" style={{ color: "#4ade80" }}>₹ {initiatedAmount.toLocaleString("en-IN")}.00</div>
            </Col>

            <Col md={3} className="text-end">
              <div className="bma-metric-lbl">UTR / Ref No.</div>
              <strong style={{ color: "#ffffff" }}>{settlement.utr_number || "Pending"}</strong>
            </Col>
          </Row>
        </div>
      </div>

      {/* BANK & SETTLEMENT INFO */}
      <Row className="g-4 mb-4">
        <Col md={6}>
          <div className="bma-card h-100 mb-0">
            <div className="bma-card-body">
              <h2 className="bma-card-title mb-3">Casting Director Bank Details</h2>
              <Row className="g-3" style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                <Col md={6}><div>Account Holder Name</div><strong style={{ color: "#ffffff" }}>{bankDetails.account_holder_name || "—"}</strong></Col>
                <Col md={6}><div>Bank Name</div><strong style={{ color: "#ffffff" }}>{bankDetails.bank_name || "—"}</strong></Col>
                <Col md={6}><div>Account Number</div><strong style={{ color: "#ffffff" }}>{bankDetails.account_number || "—"}</strong></Col>
                <Col md={6}><div>IFSC Code</div><strong style={{ color: "#ffffff" }}>{bankDetails.ifsc_code || "—"}</strong></Col>
              </Row>
            </div>
          </div>
        </Col>

        <Col md={6}>
          <div className="bma-card h-100 mb-0">
            <div className="bma-card-body">
              <h2 className="bma-card-title mb-3">Settlement Summary</h2>
              <Row className="g-3" style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                <Col md={6}><div>Settlement Type</div><strong style={{ color: "#ffffff" }}>{settlement.settlement_type || "—"}</strong></Col>
                <Col md={6}><div>Initiated Amount</div><strong style={{ color: "#4ade80" }}>₹ {initiatedAmount.toLocaleString("en-IN")}.00</strong></Col>
                <Col md={6}><div>Payment Mode</div><strong style={{ color: "#ffffff" }}>{settlement.payment_mode || "NEFT"}</strong></Col>
                <Col md={6}><div>Created Date</div><strong style={{ color: "#ffffff" }}>{settlement.created_at ? new Date(settlement.created_at).toLocaleDateString("en-IN") : "—"}</strong></Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>

      {/* TRANSACTIONS TABLE */}
      <div className="bma-card mb-4">
        <div className="bma-card-header"><h2 className="bma-card-title">Transactions ({txList.length})</h2></div>
        <div className="p-0 table-responsive">
          <table className="bma-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Project</th>
                <th>Role / Service</th>
                <th>Transaction Date</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {txList.length > 0 ? (
                txList.map((tx, idx) => (
                  <tr key={tx.id || idx}>
                    <td style={{ fontFamily: "monospace", fontWeight: "700" }}>{tx.id}</td>
                    <td>{tx.project}</td>
                    <td>{tx.role || tx.type}</td>
                    <td style={{ color: "#94a3b8" }}>{tx.date || "—"}</td>
                    <td style={{ fontWeight: "700", color: "#ffffff" }}>₹ {Number(tx.amount || tx.gross || 0).toLocaleString("en-IN")}.00</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="text-center py-4 style-muted">No transactions linked to this settlement batch.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* UPDATE UTR FORM */}
      <div className="bma-card">
        <div className="bma-card-body">
          <h2 className="bma-card-title mb-3">✏️ Update Bank UTR & Mark Complete</h2>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <label className="bma-label">UTR / Reference No. <span className="req">*</span></label>
              <input type="text" className="bma-input" placeholder="Enter UTR / Bank Reference No." value={utrNumber} onChange={(e) => setUtrNumber(e.target.value)} />
            </Col>
            <Col md={4}>
              <label className="bma-label">Payment Date</label>
              <input 
                type="date" 
                className="bma-input" 
                value={paymentDate} 
                onChange={(e) => setPaymentDate(e.target.value)} 
                style={{ colorScheme: "dark" }}
              />
            </Col>
            <Col md={4}>
              <label className="bma-label">Payment Mode</label>
              <select className="bma-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
                <option value="IMPS">IMPS</option>
                <option value="UPI">UPI</option>
              </select>
            </Col>
            <Col md={12}>
              <label className="bma-label">Remarks</label>
              <input type="text" className="bma-input" placeholder="Enter remarks..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </Col>
          </Row>

          <div className="pt-2 d-flex justify-content-end gap-2">
            <button type="button" className="bma-btn-outline" onClick={onBack}>Cancel</button>
            <button type="button" className="bma-btn-red" onClick={handleApproveComplete} disabled={submitting}>
              {submitting ? "Updating..." : "Approve & Mark Completed"}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SettlementDetailsView;